//================================================================
//  Component: Navbar
//================================================================

//  Purpose: The Navbar for Events App

//  Example:
//    <Navbar></Navbar>


//================================================================


//Libraries
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

// Contexts
import { GetUser } from '../../Library/GlobalContexts.js';

//Components
import Logout from '../../Library/Logout';
import NavItem from './NavItem';

//Images
import LLLogo from '../Images/Icon_LLLogo_Green.png';
import WorkspaceLogoD from '../Images/Icon_WorkspaceD_Black.svg';
import WorkspaceLogoC from '../Images/Icon_WorkspaceC_Black.svg';
import WorkspaceLogoP from '../Images/Icon_WorkspaceP_Black.svg';
import WorkspaceLogoI from '../Images/Icon_WorkspaceI_Black.svg';
import Expand from '../Images/Icon_Expand_Grey.svg';
import HelpDefault from '../Images/Icon_Help_Default.svg';
import HelpActive from '../Images/Icon_Help_Active.svg';

//CSS


export default function Navbar() {

  //------------------------------------------------------
  //  useContexts and React Router
  //------------------------------------------------------
  
    const getUser = useContext(GetUser);
    const routerUseLocation = useLocation();
    const dropdownRef = useRef(null);

  //------------------------------------------------------
  //  Define useStates
  //------------------------------------------------------

    // Used to change between > 'pending', 'success'
    const [pageStatus, setPageStatus] = useState('pending');

    // If the menu is open or closed
    const [menuOpen, setMenuOpen] = useState(false);

    // If the workspace menu is open or closed
    const [isOpen, setIsOpen] = useState(false);

    // Is the help icon hovered
    const [isHovered, setIsHovered] = useState(false);

    // Check if the app is in development environment
    const isDevelopment = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285';

    // Check if the app is in uat environment
    const isUAT = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396';

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

    //Returns the style of each component
    function changeStyleHandler(link, textColour) {

      if (textColour === undefined) {

        textColour = 'text-black'

      }

      //Exception for the default route '/' > We want to the home icon to be selected
      if(routerUseLocation.pathname === '/' && link.toLowerCase() === '/requests') {

        //Update the style to clicked
        return `cursor-pointer border-b-solid border-b-[#E9EBED] border-b-2 px-[10px] py-[10px] no-underline ${textColour}`;

      // Clicked item --> include child pages --> '/home' & '/home/child' are treated the same!
      } else if (routerUseLocation.pathname.toLowerCase().includes(link.split('?')[0].toLowerCase())){
    
        //Update the style to clicked
        return `cursor-pointer font-medium px-[10px] py-[10px] no-underline ${textColour}`;

      //Update the style to NOT clicked
      } else {
        
        return `cursor-pointer p-[10px] no-underline ${textColour}`;

      }

    }

    // Function to handle the click event outside of the dropdown
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

    // OnLoad
    useEffect(() => {

      if (getUser?.roles === undefined) return setPageStatus('success');

    },[pageStatus, getUser]);

    // Close the menu when clicking outside of it
    useEffect(() => {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);
    
  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

    return (
      <div className='flex flex-row items-center justify-between bg-[#141C25] text-[white] px-[25px] py-0 h-[64px]'>

        {/* ---------------------------------------------------- */}
        {/*  Logo                                                */}
        {/* ---------------------------------------------------- */}

        <div className='flex flex-row items-center justify-between p-0'>
    
          {/* Logo */}
          <div className='flex items-center gap-3 h-full mr-4 p-0'>

            <NavLink to='/requests'>
              <img className='cursor-pointer' src={LLLogo} alt='Lendlease Logo' width='48px' height='35px'></img>
            </NavLink>

            <label className='h-[40px] content-center font-medium text-lg'> Digital ERP </label>

            {/* Divider */}
            <hr className='w-[2px] h-[40px] border-0 bg-[#35404B] p-0 ml-2'></hr>

          </div>

          {/* ---------------------------------------------------- */}
          {/*  Links                                               */}
          {/* ---------------------------------------------------- */}

          <div className='flex flex-row m-0 p-0'>

            {/* Home / Requests */}
            <NavItem style={changeStyleHandler('/requests', 'text-white')} link={'/requests?view=triage'} title={'Requests'}></NavItem>

            {/* My Requests */}
            <NavItem style={changeStyleHandler('/myrequests', 'text-white')} link={'/myrequests'} title={'My Requests'}></NavItem>

          </div>

        </div>

        {/* ---------------------------------------------------- */}
        {/*  Workspace and Profile                               */}
        {/* ---------------------------------------------------- */}

        <div className='flex flex-row items-center gap-3 p-0'>

          <div className='relative inline-block' ref={dropdownRef}>

          {/* Workspace Button */}
          <div
            className={`flex flex-row gap-2 items-center justify-between bg-[rgba(255,255,255,0.10)] h-[40px] w-[250px] px-2 rounded-sm cursor-pointer ${isOpen ? 'border-solid border-[#E9EBED] border-1 rounded-md' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className='flex flex-row gap-2 items-center'>
            <img className='p-0' src={WorkspaceLogoC} alt='initials'></img>
            <div className='flex flex-col gap-0 font-light text-[11px] leading-4'>
              WORKSPACE
              <b className='font-medium text-[13px]'>Commercial</b>
            </div>
            </div>
            <img className='ml-[40px]' src={Expand} alt='expand'></img>
          </div>

          {/* Dropdown Menu */}
          <div className='absolute mt-2 rounded-md shadow-lg bg-[#262E36] w-[258px] p-2 z-50' hidden={!isOpen}>
            
            {/* Commercial */}
            <Link 
              to={isDevelopment ? 'https://devcommercial.digitalservices.lendlease.com' : isUAT  ? 'https://uatcommercial.digitalservices.lendlease.com': 'https://commercial.digitalservices.lendlease.com'} 
              className='flex flex-row gap-2 items-center hover:bg-[#394149] h-[40px] px-2 rounded-sm cursor-pointer no-underline text-inherit'
            >
              <img className='p-0' src={WorkspaceLogoC} alt='initials'></img>
              <div className='flex flex-col gap-0 font-light text-[11px] leading-4'>
                WORKSPACE
                <b className='font-medium text-[13px]'>Commercial</b>
              </div>
            </Link>

            {/* Digital Services  */}
            <Link 
              to={isDevelopment ? 'https://dev.digitalservices.lendlease.com' : isUAT ? 'https://uat.digitalservices.lendlease.com' : 'https://digitalservices.lendlease.com'} 
              className='flex flex-row gap-2 items-center hover:bg-[#394149]  h-[40px] px-2 rounded-sm cursor-pointer no-underline text-inherit'
            >
              <img className='p-0' src={WorkspaceLogoD} alt='initials'></img>
              <div className='flex flex-col gap-0 font-light text-[11px] leading-4'>
                WORKSPACE
                <b className='font-medium text-[13px]'>Digital Services </b>
              </div>
            </Link>

            {/* Demand Management */}
            <Link 
              to={isDevelopment ? 'https://devdemandmanagement.digitalservices.lendlease.com' : isUAT  ? 'https://uatdemandmanagement.digitalservices.lendlease.com': 'https://demandmanagement.digitalservices.lendlease.com'} 
              className='flex flex-row gap-2 items-center hover:bg-[#394149]  h-[40px] px-2 rounded-sm cursor-pointer no-underline text-inherit'
            >
              <img className='p-0' src={WorkspaceLogoD} alt='initials'></img>
              <div className='flex flex-col gap-0 font-light text-[11px] leading-4'>
                WORKSPACE
                <b className='font-medium text-[13px]'>Demand Management</b>
              </div>
            </Link>
            
            {/* Inventory */}
            <Link 
              to={isDevelopment ? 'https://devinventory.digitalservices.lendlease.com' : isUAT  ? 'https://uatinventory.digitalservices.lendlease.com': 'https://inventory.digitalservices.lendlease.com'}  
              className='flex flex-row gap-2 items-center hover:bg-[#394149]  h-[40px] px-2 rounded-sm cursor-pointer no-underline text-inherit'
            >
              <img className='p-0' src={WorkspaceLogoI} alt='initials'></img>
              <div className='flex flex-col gap-0 font-light text-[11px] leading-4'>
                WORKSPACE
                <b className='font-medium text-[13px]'>Inventory</b>
              </div>
            </Link>

            {/* Procurement */}
            <Link 
              to={isDevelopment ? 'https://devprocurement.digitalservices.lendlease.com' : isUAT  ? 'https://uatprocurement.digitalservices.lendlease.com': 'https://procurement.digitalservices.lendlease.com'}   
              className='flex flex-row gap-2 items-center hover:bg-[#394149]  h-[40px] px-2 rounded-sm cursor-pointer no-underline text-inherit'
            >
              <img className='p-0' src={WorkspaceLogoP} alt='initials'></img>
              <div className='flex flex-col gap-0 font-light text-[11px] leading-4'>
                WORKSPACE
                <b className='font-medium text-[13px]'>Procurement</b>
              </div>
            </Link>
            
          </div>

        </div>

          {/* Divider */}
          <hr className='w-[2px] h-[40px] border-0 bg-[#35404B] p-0'></hr>

          {/* Help / Support */}
          <Link
            to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT  ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img className='cursor-pointer' src={isHovered  ? HelpActive : HelpDefault} alt='Lendlease Logo' height='64px'></img>
          </Link>

          {/* Avatar / Profile icon */}
          <label className='cursor-pointer bg-[#8ECDCA] text-[#141c25ec] p-[7px] rounded-[50%] text-md w-[35px] h-[35px] flex justify-center items-center' onClick={() => setMenuOpen(!menuOpen)}>
              {getUser.givenname[0]}{getUser.surname[0]}
          </label>

          {/* Sign out in Menu */}
          <div hidden={!menuOpen} className='flex flex-column absolute top-[64px] right-[0px] bg-[#141c25ec] w-[130px] p-2 rounded-b'>

            {/* Log out Button */}
            <div className='flex flex-row-reverse text-center p-1'>
              <Logout></Logout>
            </div>
            
          </div>

        </div>

      </div>

    )

}
